import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Input, Button, Select, message } from 'antd';
import * as SolidIcons from '@heroicons/react/solid';
import { useQueryClient } from '@tanstack/react-query';
import SpecDrawer from '../SpecDrawer/SpecDrawer';
import SpecBottomSheet from '../SpecBottomSheet/SpecBottomSheet';
import SpecModal from '../SpecModal/SpecModal';
import SpecAlert from '../SpecAlert/SpecAlert';
import Tag from '../Tag/Tag';
import { DefaultAvatar, ChevronRightIcon, ChevronDownIcon } from '../icons';
import {
  list_band_members,
  list_available_musicians_for_band,
  add_musician_to_band,
  create_musician_and_add_to_band,
  remove_musician_from_band,
  update_band_member_role,
  Musician,
} from '../../api';
import './ManageDrawer.css';

type MemberRole = 'owner' | 'admin' | 'member';

interface BandMember {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  role: MemberRole;
  pending?: boolean;
  musicianId?: string;
}

export interface BandMembersDrawerProps {
  open: boolean;
  onClose: () => void;
  bandName: string;
  bandId?: string;
}

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

const ROLE_ORDER: MemberRole[] = ['owner', 'admin', 'member'];

// Convert API Musician to display BandMember
const musicianToBandMember = (musician: Musician & { role?: string }): BandMember => ({
  id: musician.id,
  name: `${musician.first_name} ${musician.last_name}`.trim(),
  avatarUrl: musician.photo_url,
  role: (musician.role as MemberRole) || 'member',
  musicianId: musician.id,
});

export const BandMembersDrawer: React.FC<BandMembersDrawerProps> = ({
  open,
  onClose,
  bandName,
  bandId,
}) => {
  const queryClient = useQueryClient();
  const [members, setMembers] = useState<BandMember[]>([]);
  const [availableMusicians, setAvailableMusicians] = useState<Musician[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Add member state
  const [addMemberMode, setAddMemberMode] = useState<'select' | 'create' | null>(null);
  const [selectedMusicianId, setSelectedMusicianId] = useState<string | null>(null);
  const [newMusicianFirstName, setNewMusicianFirstName] = useState('');
  const [newMusicianLastName, setNewMusicianLastName] = useState('');
  const [addRole, setAddRole] = useState<MemberRole>('member');
  const [adding, setAdding] = useState(false);

  // Modal state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferPasscode, setTransferPasscode] = useState('');
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removePasscode, setRemovePasscode] = useState('');

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastKeyRef = useRef(0);

  const showToast = useCallback((msg: string) => {
    toastKeyRef.current += 1;
    setToastMessage(msg);
  }, []);

  // Load members and available musicians when drawer opens
  useEffect(() => {
    if (open && bandId) {
      loadMembers();
      loadAvailableMusicians();
    }
  }, [open, bandId]);

  const loadMembers = async () => {
    if (!bandId) return;
    setLoading(true);
    try {
      const musicians = await list_band_members(bandId);
      setMembers(musicians.map((m) => musicianToBandMember(m)));
    } catch (error) {
      console.error('Failed to load band members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableMusicians = async () => {
    if (!bandId) return;
    try {
      const musicians = await list_available_musicians_for_band(bandId);
      setAvailableMusicians(musicians);
    } catch (error) {
      console.error('Failed to load available musicians:', error);
    }
  };

  const selectedMember = members.find((m) => m.id === selectedId) || null;
  const sortedMembers = [...members].sort((a, b) => {
    if (a.pending && !b.pending) return 1;
    if (!a.pending && b.pending) return -1;
    return ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
  });

  const handleSelectMember = (id: string) => {
    setSelectedId(id);
  };

  const closeDetailSheet = () => {
    setSelectedId(null);
  };

  const handleAddExistingMusician = async () => {
    if (!bandId || !selectedMusicianId) return;

    setAdding(true);
    try {
      await add_musician_to_band(bandId, selectedMusicianId, addRole);
      await loadMembers();
      await loadAvailableMusicians();
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'musicians'] });
      setSelectedMusicianId(null);
      setAddMemberMode(null);
      showToast('Member Added');
    } catch (error: any) {
      message.error(error.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleCreateAndAddMusician = async () => {
    if (!bandId || !newMusicianFirstName.trim() || !newMusicianLastName.trim()) return;

    setAdding(true);
    try {
      await create_musician_and_add_to_band(
        bandId,
        {
          first_name: newMusicianFirstName.trim(),
          last_name: newMusicianLastName.trim(),
        },
        addRole
      );
      await loadMembers();
      await loadAvailableMusicians();
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'musicians'] });
      queryClient.invalidateQueries({ queryKey: ['musicians'] });
      setNewMusicianFirstName('');
      setNewMusicianLastName('');
      setAddMemberMode(null);
      showToast('Member Created & Added');
    } catch (error: any) {
      message.error(error.message || 'Failed to create musician');
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (role: MemberRole) => {
    if (!selectedMember || !bandId || !selectedMember.musicianId) return;
    if (role === 'owner') {
      setTransferModalOpen(true);
      return;
    }

    try {
      await update_band_member_role(bandId, selectedMember.musicianId, role);
      // Reload members from server to ensure sync
      await loadMembers();
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'musicians'] });
      closeDetailSheet();
      showToast('Role Changed');
    } catch (error: any) {
      message.error(error.message || 'Failed to update role');
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedMember || !bandId || !selectedMember.musicianId) return;

    try {
      // Find current owner and demote to admin
      const currentOwner = members.find((m) => m.role === 'owner');
      if (currentOwner && currentOwner.musicianId) {
        await update_band_member_role(bandId, currentOwner.musicianId, 'admin');
      }

      // Promote selected member to owner
      await update_band_member_role(bandId, selectedMember.musicianId, 'owner');

      // Update local state
      setMembers((prev) =>
        prev.map((m) => {
          if (m.id === selectedMember.id) return { ...m, role: 'owner' as MemberRole };
          if (m.role === 'owner') return { ...m, role: 'admin' as MemberRole };
          return m;
        })
      );
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'musicians'] });
      setTransferModalOpen(false);
      setTransferPasscode('');
      closeDetailSheet();
      showToast('Ownership Transfer Complete');
    } catch (error: any) {
      message.error(error.message || 'Failed to transfer ownership');
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedMember || !bandId || !selectedMember.musicianId) return;

    try {
      await remove_musician_from_band(bandId, selectedMember.musicianId);
      await loadMembers();
      await loadAvailableMusicians();
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'musicians'] });
      setRemoveModalOpen(false);
      setRemovePasscode('');
      closeDetailSheet();
      showToast('Member Removed');
    } catch (error: any) {
      message.error(error.message || 'Failed to remove member');
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    setAddMemberMode(null);
    setSelectedMusicianId(null);
    setNewMusicianFirstName('');
    setNewMusicianLastName('');
    onClose();
  };

  const customTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div
        style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          margin: '0 auto 24px',
          flexShrink: 0,
        }}
      />
      Band Members
      <p className="manage-drawer__subtitle" style={{ marginTop: 8 }}>Add or remove members of band</p>
    </div>
  );

  return (
    <>
      <SpecDrawer
        title={customTitle}
        open={open}
        onClose={handleClose}
        size="large"
        swipeable={false}
        closable={false}
      >
        <div className="manage-drawer-content">
          {/* Add Member Section */}
          {bandId && (
            <div className="manage-drawer__invite">
              {!addMemberMode ? (
                <div className="manage-drawer__add-options">
                  <Button
                    type="primary"
                    shape="round"
                    block
                    onClick={() => setAddMemberMode('select')}
                    disabled={availableMusicians.length === 0}
                  >
                    Add Existing Musician
                  </Button>
                  <Button
                    shape="round"
                    block
                    onClick={() => setAddMemberMode('create')}
                    style={{ marginTop: 8 }}
                  >
                    Create New Musician
                  </Button>
                </div>
              ) : addMemberMode === 'select' ? (
                <div className="manage-drawer__add-form">
                  <span className="manage-drawer__invite-label">Select Musician</span>
                  <Select
                    showSearch
                    placeholder="Search musicians..."
                    value={selectedMusicianId}
                    onChange={setSelectedMusicianId}
                    style={{ width: '100%', marginBottom: 12 }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={availableMusicians.map((m) => ({
                      value: m.id,
                      label: `${m.first_name} ${m.last_name}`,
                    }))}
                  />
                  <div className="manage-drawer__invite-row">
                    <Select
                      value={addRole}
                      onChange={(val) => setAddRole(val as MemberRole)}
                      style={{ width: 120 }}
                      options={[
                        { value: 'member', label: 'Member' },
                        { value: 'admin', label: 'Admin' },
                      ]}
                    />
                    <Button
                      type="primary"
                      shape="round"
                      onClick={handleAddExistingMusician}
                      disabled={!selectedMusicianId}
                      loading={adding}
                    >
                      Add
                    </Button>
                    <Button shape="round" onClick={() => { setAddMemberMode(null); setSelectedMusicianId(null); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="manage-drawer__add-form">
                  <span className="manage-drawer__invite-label">Create New Musician</span>
                  <Input
                    placeholder="First Name"
                    value={newMusicianFirstName}
                    onChange={(e) => setNewMusicianFirstName(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <Input
                    placeholder="Last Name"
                    value={newMusicianLastName}
                    onChange={(e) => setNewMusicianLastName(e.target.value)}
                    style={{ marginBottom: 12 }}
                  />
                  <div className="manage-drawer__invite-row">
                    <Select
                      value={addRole}
                      onChange={(val) => setAddRole(val as MemberRole)}
                      style={{ width: 120 }}
                      options={[
                        { value: 'member', label: 'Member' },
                        { value: 'admin', label: 'Admin' },
                      ]}
                    />
                    <Button
                      type="primary"
                      shape="round"
                      onClick={handleCreateAndAddMusician}
                      disabled={!newMusicianFirstName.trim() || !newMusicianLastName.trim()}
                      loading={adding}
                    >
                      Create & Add
                    </Button>
                    <Button shape="round" onClick={() => { setAddMemberMode(null); setNewMusicianFirstName(''); setNewMusicianLastName(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Members list */}
          <>
            <span className="manage-drawer__section-label">Members</span>
            {loading ? (
              <div className="manage-drawer__loading">Loading members...</div>
            ) : members.length === 0 ? (
              <div className="manage-drawer__empty">No members yet</div>
            ) : (
              <div className="manage-drawer__list">
                {sortedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="manage-drawer__person"
                    onClick={() => handleSelectMember(member.id)}
                  >
                    <div className="manage-drawer__person-avatar">
                      {member.avatarUrl ? (
                        <img src={member.avatarUrl} alt={member.name} />
                      ) : (
                        <DefaultAvatar size={40} />
                      )}
                    </div>
                    <div className="manage-drawer__person-info">
                      <span className="manage-drawer__person-name">{member.name}</span>
                      {member.email && (
                        <span className="manage-drawer__person-email">
                          {member.email}{member.pending && <i> (invite sent)</i>}
                        </span>
                      )}
                    </div>
                    <div className="manage-drawer__person-role">
                      {member.role === 'owner' ? (
                        <Tag>{ROLE_LABELS[member.role]}</Tag>
                      ) : (
                        <span className="manage-drawer__role-btn">
                          {member.pending ? 'Pending' : ROLE_LABELS[member.role]}
                          <ChevronDownIcon size="xs" tone="white" />
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        </div>
      </SpecDrawer>

      {/* Member detail bottom sheet */}
      <SpecBottomSheet
        open={!!selectedMember}
        onClose={closeDetailSheet}
      >
        {selectedMember && (
          <>
            {/* Header */}
            <div className="manage-detail-sheet__header">
              <div className="manage-detail-sheet__avatar">
                {selectedMember.avatarUrl ? (
                  <img src={selectedMember.avatarUrl} alt={selectedMember.name} />
                ) : (
                  <DefaultAvatar size={24} />
                )}
              </div>
              <div className="manage-detail-sheet__info">
                <span className="manage-detail-sheet__name">{selectedMember.name}</span>
                {selectedMember.email && (
                  <span className="manage-detail-sheet__status">
                    {selectedMember.email}
                    {selectedMember.pending && <i> (invite sent)</i>}
                  </span>
                )}
              </div>
            </div>

            {/* Role rows */}
            <div className="action-sheet-items">
              {ROLE_ORDER.map((role) => (
                <button
                  key={role}
                  className="action-sheet-item"
                  onClick={() => handleRoleChange(role)}
                >
                  <span className="action-sheet-item-label">{ROLE_LABELS[role]}</span>
                  {selectedMember.role === role && (
                    <span className="manage-detail-sheet__role-check">
                      <SolidIcons.CheckCircleIcon />
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '0 16px' }} />

            {/* Action rows */}
            <div className="action-sheet-items">
              {selectedMember.role !== 'owner' && (
                <button
                  className="action-sheet-item"
                  onClick={() => setTransferModalOpen(true)}
                >
                  <span className="action-sheet-item-label">Transfer Ownership</span>
                  <ChevronRightIcon size="l" tone="darkGrey" />
                </button>
              )}
              <button
                className="action-sheet-item action-sheet-item-danger"
                onClick={() => setRemoveModalOpen(true)}
              >
                <span className="action-sheet-item-label">Remove</span>
                <ChevronRightIcon size="l" tone="darkGrey" />
              </button>
            </div>
          </>
        )}
      </SpecBottomSheet>

      {/* Transfer Ownership Modal */}
      <SpecModal
        open={transferModalOpen}
        onCancel={() => {
          setTransferModalOpen(false);
          setTransferPasscode('');
        }}
        title="Transfer Band Ownership"
        footer={null}
        closable={false}
        centered
        width={340}
        zIndex={2200}
      >
        <p className="manage-drawer__modal-body">
          Type "transferownership" to transfer ownership of {bandName} to{' '}
          {selectedMember?.name || 'this member'}:
        </p>
        <div className="manage-drawer__modal-form">
          <span className="manage-drawer__modal-label">Transfer Passcode</span>
          <Input
            placeholder="Enter passcode to transfer"
            value={transferPasscode}
            onChange={(e) => setTransferPasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={transferPasscode !== 'transferownership'}
            onClick={handleTransferOwnership}
          >
            Transfer Ownership
          </Button>
        </div>
      </SpecModal>

      {/* Remove Member Modal */}
      <SpecModal
        open={removeModalOpen}
        onCancel={() => {
          setRemoveModalOpen(false);
          setRemovePasscode('');
        }}
        title="Remove Band Member"
        footer={null}
        closable={false}
        centered
        width={340}
        zIndex={2200}
      >
        <p className="manage-drawer__modal-body">
          Type "removebandmember" to remove {selectedMember?.name || 'this member'} from {bandName}:
        </p>
        <div className="manage-drawer__modal-form">
          <span className="manage-drawer__modal-label">Remove Passcode</span>
          <Input
            placeholder="Enter passcode to remove"
            value={removePasscode}
            onChange={(e) => setRemovePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={removePasscode !== 'removebandmember'}
            onClick={handleRemoveMember}
          >
            Remove Band Member
          </Button>
        </div>
      </SpecModal>

      <SpecAlert
        key={toastKeyRef.current}
        message={toastMessage}
        onDone={() => setToastMessage(null)}
      />
    </>
  );
};

export default BandMembersDrawer;
